import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { convertFilterToDateRange } from '../../../utils/dateUtils';

export interface DateFilter {
  type: 'today' | 'past7days' | 'past90days' | 'monthToDate' | 'yearToDate' | 'custom';
  startDate?: string;
  endDate?: string;
}

export interface MetricData {
  id: string;
  name: string;
  description: string;
  value: number | object;
  displayType:
    | 'number'
    | 'currency'
    | 'percentage'
    | 'decimal'
    | 'table'
    | 'score'
    | 'object'
    | 'nps-chart'
    | 'leaderboard'
    | 'time-distribution';
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  chartData?: ChartDataPoint[];
  category: string;
  color: string;
  icon: string;
}

export interface ChartDataPoint {
  date: string;
  value: number;
}

export interface DashboardCategory {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  metricIds: string[];
}

interface Dashboard {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  categories: DashboardCategory[];
}

export interface MetricsResponse {
  dashboardId: string;
  metrics: MetricData[];
  filter: DateFilter;
  generatedAt: string;
}

interface DashboardsResponse {
  dashboards: Dashboard[];
}

// Future: Consider migrating to shared types from libs/analytics-types
// For now, keeping existing types to maintain stability

const analyticsService = () => {
  const getDashboards = async (): Promise<DashboardsResponse> => {
    // eslint-disable-next-line require-await
    return ChiroUpAPI.get('api', '/analytics/dashboards', {});
  };

  const getMetrics = async (
    dashboardId: string,
    filter: DateFilter,
    tab?: string
  ): Promise<MetricsResponse> => {
    // 🌍 TIMEZONE FIX: Convert filter types to explicit dates using client's local timezone
    // This ensures "today" means the user's today, not the server's today
    // Fixes issue where users in different timezones see incorrect data
    let apiFilter = filter;

    if (filter.type !== 'custom') {
      // Convert filter type to explicit date range in user's timezone
      const { startDate, endDate } = convertFilterToDateRange(filter.type);
      apiFilter = {
        type: 'custom',
        startDate,
        endDate,
      };
      console.log(
        `🌍 Timezone Conversion: ${filter.type} → ${startDate} to ${endDate} (client timezone)`
      );
    }

    // Use cached endpoint for Stripe dashboard for instant performance (53ms vs 5+ min)
    // All other dashboards continue to use direct API
    const USE_CACHED_STRIPE = true; // ✅ ENABLED - Cache system is now production-ready!
    let endpoint: string;

    if (dashboardId === 'stripe' && USE_CACHED_STRIPE) {
      endpoint = `/analytics/metrics-cached/${dashboardId}`; // Fast cached endpoint with fallback
    } else if (dashboardId === 'db-stats') {
      endpoint = `/analytics/db-stats`; // Dedicated endpoint for database statistics
    } else {
      endpoint = `/analytics/metrics/${dashboardId}`; // Original direct API endpoint
    }

    console.log(`🚀 Analytics Service: Using endpoint ${endpoint} for dashboard ${dashboardId}`);

    // eslint-disable-next-line require-await
    return ChiroUpAPI.post('api', endpoint, {
      body: {
        dashboardId,
        filter: apiFilter, // Send converted filter with explicit dates
        ...(tab ? { tab } : {}),
      },
    });
  };

  const getDbStatsClinic = async (
    clinicId: number,
    filter: DateFilter
  ): Promise<MetricsResponse> => {
    let apiFilter = filter;

    if (filter.type !== 'custom') {
      const { startDate, endDate } = convertFilterToDateRange(filter.type);
      apiFilter = {
        type: 'custom',
        startDate,
        endDate,
      };
    }

    return ChiroUpAPI.post('api', `/analytics/db-stats/clinic/${clinicId}`, {
      body: {
        filter: apiFilter,
      },
    });
  };

  const getDbStatsTableClinics = async (
    tableName: string,
    filter: DateFilter
  ): Promise<MetricsResponse> => {
    let apiFilter = filter;

    if (filter.type !== 'custom') {
      const { startDate, endDate } = convertFilterToDateRange(filter.type);
      apiFilter = {
        type: 'custom',
        startDate,
        endDate,
      };
    }

    return ChiroUpAPI.post(
      'api',
      `/analytics/db-stats/table/${encodeURIComponent(tableName)}/clinics`,
      {
        body: {
          filter: apiFilter,
        },
      }
    );
  };

  return {
    getDashboards,
    getMetrics,
    getDbStatsClinic,
    getDbStatsTableClinics,
  };
};

export default analyticsService();
